'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'motion/react'
import {
  MonitorIcon,
  ChalkboardIcon,
  ShuffleAngularIcon,
  type Icon,
} from '@phosphor-icons/react'
import { ApplyButton } from '@/components/apply/ApplyButton'

interface LearningFormat {
  name?: string
  details?: string
}

interface LearningFormatViewProps {
  eyebrow: string
  title: string
  description: string
  formats: LearningFormat[]
  tags: string[]
  featuredBadge: string
  locale?: 'en' | 'ja'
}

const FORMAT_ICONS: Icon[] = [MonitorIcon, ChalkboardIcon, ShuffleAngularIcon]

/** Per-card accents — never red+green on the same surface (brand guideline) */
const FORMAT_STYLES = [
  {
    card: 'bg-white border-brand-green/20 hover:border-brand-green/40',
    iconBg: 'bg-brand-green-soft border-brand-green/25',
    iconColor: 'text-brand-green',
    glow: 'bg-brand-green/10',
    tag: 'bg-brand-green-soft text-brand-green',
  },
  {
    card: 'bg-white border-brand-navy/15 hover:border-brand-navy/30',
    iconBg: 'bg-brand-navy-soft border-brand-navy/20',
    iconColor: 'text-brand-navy',
    glow: 'bg-brand-navy/8',
    tag: 'bg-brand-navy-soft text-brand-navy',
  },
  {
    card: 'bg-white border-brand-gold/35 hover:border-brand-gold/55 ring-1 ring-brand-gold/20',
    iconBg: 'bg-brand-gold-soft border-brand-gold/40',
    iconColor: 'text-brand-charcoal',
    glow: 'bg-brand-gold/15',
    tag: 'bg-brand-gold-soft text-brand-charcoal',
  },
]

const fadeUp = {
  hidden: { y: 20 },
  visible: {
    y: 0,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const },
  },
}

function FormatCard({
  format,
  index,
  reducedMotion,
  isFeatured,
  tag,
  featuredBadge,
  locale = 'en',
}: {
  locale?: 'en' | 'ja'
  format: LearningFormat
  index: number
  reducedMotion: boolean
  isFeatured: boolean
  tag: string
  featuredBadge: string
}) {
  const Icon = FORMAT_ICONS[index % FORMAT_ICONS.length]
  const style = FORMAT_STYLES[index % FORMAT_STYLES.length]

  return (
    <motion.li
      variants={fadeUp}
      className={`group relative flex flex-col rounded-2xl border p-6 md:p-8 shadow-[0_1px_2px_rgba(28,31,38,0.04)] transition-[box-shadow,border-color] duration-300 hover:shadow-[0_24px_48px_-28px_rgba(20,33,61,0.15)] ${style.card} ${
        isFeatured ? 'lg:-mt-2 lg:mb-2' : ''
      }`}
      whileHover={reducedMotion ? undefined : { y: -6 }}
      transition={{ type: 'spring', stiffness: 380, damping: 26 }}
    >
      {/* Corner glow — gold/green/navy only, never combined */}
      <motion.div
        className={`pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full blur-2xl ${style.glow}`}
        animate={reducedMotion ? undefined : { opacity: [0.3, 0.55, 0.3] }}
        transition={{ duration: 5 + index, repeat: Infinity, ease: 'easeInOut' }}
        aria-hidden
      />

      {isFeatured && featuredBadge && (
        <span className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full border border-brand-gold/50 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-charcoal shadow-sm">
          <span className="h-1 w-1 rounded-full bg-brand-gold" aria-hidden />
          {featuredBadge}
        </span>
      )}

      <div className="mb-6 flex items-start justify-between gap-4">
        <motion.span
          className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border ${style.iconBg}`}
          initial={reducedMotion ? false : { rotate: -8, scale: 0.85 }}
          whileInView={{ rotate: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 + index * 0.1, type: 'spring', stiffness: 300 }}
        >
          <Icon size={26} weight="duotone" className={style.iconColor} />
        </motion.span>
        <span className="font-mono text-sm text-brand-gold tabular-nums">
          {String(index + 1).padStart(2, '0')}
        </span>
      </div>

      {tag && (
        <span
          className={`mb-3 inline-block w-fit rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${style.tag}`}
        >
          {tag}
        </span>
      )}

      <h3 className="type-card-title">{format.name}</h3>

      {format.details && (
        <p className="type-body mt-3 flex-1">{format.details}</p>
      )}

      {/* Whole card links to registration, where the format can be chosen */}
      <ApplyButton
        className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red after:absolute after:inset-0 after:rounded-2xl after:content-[''] focus-ring rounded-sm"
      >
        {locale === 'ja' ? 'この形式で申し込む' : 'Register for this format'}
        <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1">→</span>
      </ApplyButton>

      {/* Gold accent line — premium touch per brand guide */}
      <div className="mt-6 flex items-center gap-3" aria-hidden>
        <motion.div
          className="h-px flex-1 bg-brand-cool-gray origin-left"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 + index * 0.12, duration: 0.5 }}
        />
        <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" />
      </div>
    </motion.li>
  )
}

export function LearningFormatView({
  eyebrow,
  title,
  description,
  formats,
  tags,
  featuredBadge,
  locale = 'en',
}: LearningFormatViewProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-72px' })
  const reducedMotion = useReducedMotion()

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reducedMotion ? 0 : 0.12,
        delayChildren: reducedMotion ? 0 : 0.2,
      },
    },
  }

  return (
    <section
      id="formats"
      ref={sectionRef}
      className="section-padding relative overflow-hidden"
    >
      {/* Thin gold accent — brand premium touch */}
      <motion.div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/50 to-transparent"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        aria-hidden
      />

      {/* Subtle navy ambient — foundation color, not dominant */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-brand-navy/[0.03] to-transparent"
        aria-hidden
      />

      <motion.div
        className="section-container relative max-w-6xl"
        initial={false}
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div
          className="section-header mb-12 md:mb-16"
          variants={fadeUp}
        >
          <span className="section-eyebrow section-eyebrow-centered">{eyebrow}</span>
          <h2 className="type-section-title">{title}</h2>
          <p className="type-section-lead mx-auto">{description}</p>
        </motion.div>

        {/* Cards + connector line (desktop) */}
        <motion.div className="relative" variants={containerVariants}>
          {/* Gold connector — desktop only */}
          <div
            className="pointer-events-none absolute left-[16%] right-[16%] top-[4.5rem] hidden lg:block h-px"
            aria-hidden
          >
            <motion.div
              className="h-full bg-gradient-to-r from-brand-gold/0 via-brand-gold/40 to-brand-gold/0"
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ delay: 0.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            />
          </div>

          <motion.ol
            className="grid gap-5 md:gap-6 lg:grid-cols-3 lg:gap-5 items-stretch"
            variants={containerVariants}
            initial={false}
            animate="visible"
          >
            {formats.map((fmt, idx) => (
              <FormatCard
                key={idx}
                format={fmt}
                index={idx}
                reducedMotion={!!reducedMotion}
                isFeatured={idx === formats.length - 1}
                tag={tags[idx] ?? ''}
                featuredBadge={featuredBadge}
                locale={locale}
              />
            ))}
          </motion.ol>
        </motion.div>
      </motion.div>
    </section>
  )
}
